import {
  Api,
  ContentType,
  EditIrrigationBody,
  GetIrrigationGroupingResponse,
  GetIrrigationsResponse,
  GetPeakHoursResponse,
  IrrigationCommandResponse,
  SetPeakHourBody,
  SetPeakHourResponse,
  StartIrrigationBody,
} from './apiTypes';
import { ConfigState } from '../../components/Highcharts/utils/DayMonthBaseForm';
import { generateIdempotencyKey } from '../../utils/idempotencyKey';
import { withSessionCheck } from '../../utils/sessionErrors';

// Define a securityWorker function
const securityWorker = (token: string | null) => {
  if (token) {
    return {
      headers: {
        Authorization: token,
      },
    };
  }
  return {
    headers: {
      Authorization: '',
    },
  };
};

// Modify your apiClient initialization to include the securityWorker
const apiClient = new Api({
  baseUrl: '/api',
  securityWorker,
  customFetch: withSessionCheck((...params) => fetch(...params)),
});

// Default env api token to access basic endpoints
apiClient.setSecurityData('');

export const irrigation = {
  getIrrigations: (
    farmId: string,
    query: { initTime: string; endTime: string },
  ) => apiClient.request<GetIrrigationsResponse>({
    path: `/farms/${farmId}/irrigations`,
    method: 'GET',
    query,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  getIrrigationGrouping: (
    farmId: string,
  ) => apiClient.request<GetIrrigationGroupingResponse>({
    path: `/farms/${farmId}/irrigation-grouping`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  start: (
    farmId: string,
    input: Omit<StartIrrigationBody, 'idempotencyKey'>,
  ) => apiClient.request<IrrigationCommandResponse>({
    path: `/farms/${farmId}/irrigations`,
    method: 'POST',
    body: { ...input, idempotencyKey: generateIdempotencyKey() },
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  stop: (
    farmId: string,
    wiseconnIrrigationId: number,
    wiseconnZoneId?: number,
  ) => apiClient.request<IrrigationCommandResponse>({
    path: `/farms/${farmId}/irrigations/${wiseconnIrrigationId}/stop`,
    method: 'PATCH',
    body: { idempotencyKey: generateIdempotencyKey(), wiseconnZoneId } as EditIrrigationBody,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  activate: (
    farmId: string,
    wiseconnIrrigationId: number,
  ) => apiClient.request<IrrigationCommandResponse>({
    path: `/farms/${farmId}/irrigations/${wiseconnIrrigationId}/activate`,
    method: 'PATCH',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  remove: (
    farmId: string,
    wiseconnIrrigationId: number,
    wiseconnZoneId?: number,
  ) => apiClient.request<IrrigationCommandResponse>({
    path: `/farms/${farmId}/irrigations/${wiseconnIrrigationId}`,
    method: 'DELETE',
    body: { idempotencyKey: generateIdempotencyKey(), wiseconnZoneId } as EditIrrigationBody,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const peakHour = {
  getPeakHours: (
    farmId: string,
  ) => apiClient.request<GetPeakHoursResponse>({
    path: `/farms/${farmId}/peak-hours`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  setPeakHour: (
    farmId: string,
    pumpSystemId: number,
    body: SetPeakHourBody,
  ) => apiClient.request<SetPeakHourResponse>({
    path: `/farms/${farmId}/pump-systems/${pumpSystemId}/peak-hour`,
    method: 'PUT',
    body,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const coldHoursConfig = {
  get: (sectorId: string) => apiClient.request({
    path: `/coldHoursConfig/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  update: (sectorId: string, data: ConfigState) => apiClient.request({
    path: `/coldHoursConfig/${sectorId}`,
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const chillPortionsConfig = {
  get: (sectorId: string) => apiClient.request({
    path: `/chillPortionsConfig/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  update: (sectorId: string, data: ConfigState) => apiClient.request({
    path: `/chillPortionsConfig/${sectorId}`,
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const degreeDayConfig = {
  get: (sectorId: string) => apiClient.request({
    path: `/degreeDayConfig/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  post: (sectorId: string, data: {
    accumulatedDegreesBaseTemp: number,
    accumulatedColdHoursBaseTemp: number,
  }) => apiClient.request({
    path: `/degreeDayConfig/${sectorId}`,
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const biweeklyReport = {
  biweeklyReportList: (sectorId: string) => apiClient.request({
    path: `/biweekly-reports/sector/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  biweeklyReportDetail: (reportId: string) => apiClient.request({
    path: `/biweekly-reports/${reportId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  biweeklyReportSummary: (reportId: string) => apiClient.request({
    path: `/biweekly-reports/${reportId}/summary`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  biweeklyReportCreate: (data: {
    sectorId: string,
    startDate: string,
    endDate: string,
    createdBy: string,
    selectedCharts?: string[],
    technicalComment?: string,
    technicalAdministrationComment?: string,
    operationComment?: string,
  }) => apiClient.request({
    path: '/biweekly-reports',
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  biweeklyReportUpdate: (reportId: string, data: {
    startDate?: string,
    endDate?: string,
    selectedCharts?: string[],
    technicalComment?: string,
    technicalAdministrationComment?: string,
    operationComment?: string,
  }) => apiClient.request({
    path: `/biweekly-reports/${reportId}`,
    method: 'PUT',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  biweeklyReportDelete: (reportId: string) => apiClient.request({
    path: `/biweekly-reports/${reportId}`,
    method: 'DELETE',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const alert = {
  alertList: (
    page: string,
    rowsPerPage: string,
    sectorId?: string,
    chartName?: string,
    farmId?: string,
  ) => apiClient.request({
    path: `/alert?page=${page}&rowsPerPage=${rowsPerPage}${sectorId ? `&sectorId=${sectorId}` : ''}${chartName ? `&chartName=${chartName}` : ''}${farmId ? `&farmId=${farmId}` : ''}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  alertDetail: (id: string) => apiClient.request({
    path: `/alert/${id}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  alertCreate: (data: any) => apiClient.request({
    path: '/alert',
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  alertUpdate: (id: string, data: any) => apiClient.request({
    path: `/alert/${id}`,
    method: 'PATCH',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  alertDelete: (id: string) => apiClient.request({
    path: `/alert/${id}`,
    method: 'DELETE',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const rainfallConfig = {
  get: (sectorId: string) => apiClient.request({
    path: `/rainfallConfig/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  update: (sectorId: string, years: {
    [year: string]: {
      rainfallStartMonth: number;
      rainfallStartDay: number;
      rainfallEndMonth: number;
      rainfallEndDay: number;
    };
  }) => apiClient.request({
    path: `/rainfallConfig/${sectorId}`,
    method: 'POST',
    body: { years },
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customNodes = {
  nodesList: (data: {
    page: number;
    limit: number;
    searchValue?: string;
    accountId?: string;
    farmId?: string;
    status?: string;
    adminView?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', data.page.toString());
    if (data.limit) queryParams.append('limit', data.limit.toString());
    if (data.searchValue) queryParams.append('searchValue', data.searchValue);
    if (data.accountId) queryParams.append('accountId', data.accountId);
    if (data.farmId) queryParams.append('farmId', data.farmId);
    if (data.status) queryParams.append('status', data.status);
    if (data.adminView) queryParams.append('adminView', data.adminView);
    return apiClient.request({
      path: `/nodes?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
  nodesSyncBatch: (nodes: any[]) => apiClient.request({
    path: '/nodes/sync/batch',
    method: 'POST',
    body: { nodes },
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  devicesSyncBatchAllFarms: () => apiClient.request<{
    success: boolean;
    message: string;
    totals: {
      farms: number;
      nodes: { new: number; created: number; failed: number };
      sensors: { new: number; created: number; failed: number };
      skipped: number;
    };
    perFarm: Array<{
      farmId: string;
      farmName: string;
      nodes?: { new: number; created: number; failed: number };
      sensors?: { new: number; created: number; failed: number };
      error?: string;
    }>;
  }>({
    path: '/devices/sync/batch/all-farms',
    method: 'POST',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  nodesSummary: (data: { accountId?: string, farmId?: string }) => apiClient.request({
    path: `/nodes/summary?${data.accountId ? `accountId=${data.accountId}&` : ''}${data.farmId ? `farmId=${data.farmId}` : ''}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customSensors = {
  sensorsList: (data: {
    page: number;
    limit: number;
    searchValue?: string;
    accountId?: string;
    farmId?: string;
    sensorType?: string;
    adminView?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', data.page.toString());
    if (data.limit) queryParams.append('limit', data.limit.toString());
    if (data.searchValue) queryParams.append('searchValue', data.searchValue);
    if (data.accountId) queryParams.append('accountId', data.accountId);
    if (data.farmId) queryParams.append('farmId', data.farmId);
    if (data.sensorType) queryParams.append('sensorType', data.sensorType);
    if (data.adminView) queryParams.append('adminView', data.adminView);
    return apiClient.request({
      path: `/sensors?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
  sensorsSyncBatch: (sensors: any[]) => apiClient.request({
    path: '/sensors/sync/batch',
    method: 'POST',
    body: { sensors },
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customSensorModels = {
  sensorModelsList: (data: {
    page: number;
    limit: number;
    searchValue?: string;
    accountId?: string;
    farmId?: string;
    status?: string;
    type?: string;
    sensorType?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', data.page.toString());
    if (data.limit) queryParams.append('limit', data.limit.toString());
    if (data.searchValue) queryParams.append('searchValue', data.searchValue);
    if (data.accountId) queryParams.append('accountId', data.accountId);
    if (data.farmId) queryParams.append('farmId', data.farmId);
    if (data.status) queryParams.append('status', data.status);
    if (data.type) queryParams.append('type', data.type);
    if (data.sensorType) queryParams.append('sensorType', data.sensorType);
    return apiClient.request({
      path: `/sensorModels?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
  sensorModelsSummary: (data: { accountId?: string,
    farmId?: string,
    sensorType?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('accountId', data.accountId || '');
    queryParams.append('farmId', data.farmId || '');
    return apiClient.request({
      path: `/sensorModels/summary?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
};

export const supportTicket = {
  send: (data: {
    accountName: string;
    farmName: string;
    deviceName: string;
    deviceType: string;
    senderName: string;
    senderEmail: string;
    sectorName?: string;
  }) => apiClient.request({
    path: '/support-ticket',
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customSectors = {
  wiseconnZones: (farmWiseconnId: string) => apiClient.request<{
    success: boolean;
    message: string;
    zones: Array<{
      id: number;
      name: string;
      area: number;
      areaUnit: string;
      polygon: Array<{ lat: number; lng: number }>;
      crops: any[];
      type: string;
    }>;
  }>({
    path: `/sectors/wiseconn/${farmWiseconnId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  sectorsSyncBatch: (sectors: Array<{
    name: string;
    wiseconnId: string;
    farm: string;
    theoreticalSurface: string;
    coordinates?: Array<{ lat: number; lng: number }>;
  }>) => apiClient.request<{
    success: boolean;
    message: string;
    createdSectors: Array<{ _id: string; wiseconnId: string; name: string }>;
    errors: Array<{ sectorData: any; error: string }>;
    summary: { total: number; created: number; failed: number };
  }>({
    path: '/sectors/sync/batch',
    method: 'POST',
    body: { sectors },
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  sectorsSyncBatchAllFarms: () => apiClient.request<{
    success: boolean;
    message: string;
    totals: {
      farms: number;
      new: number;
      created: number;
      failed: number;
      skipped: number;
    };
    perFarm: Array<{
      farmId: string;
      farmName: string;
      new?: number;
      created?: number;
      failed?: number;
      error?: string;
    }>;
  }>({
    path: '/sectors/sync/batch/all-farms',
    method: 'POST',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customFarms = {
  listWithCounts: (data: {
    page: number;
    limit: number;
    searchValue?: string;
    accountId?: string;
    sort?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', data.page.toString());
    queryParams.append('limit', data.limit.toString());
    if (data.searchValue) queryParams.append('searchValue', data.searchValue);
    if (data.accountId) queryParams.append('accountId', data.accountId);
    if (data.sort) queryParams.append('sort', data.sort);
    return apiClient.request<{
      success: boolean;
      message: string;
      farms: Array<{
        _id: string;
        name: string;
        accountId?: string;
        accountName?: string;
        sectorCount: number;
        nodeCount: number;
        sensorCount: number;
      }>;
      totalFarms: number;
    }>({
      path: `/farms/with-counts?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
  wiseconnCount: () => apiClient.request<{
    success: boolean;
    message: string;
    count: number;
  }>({
    path: '/farms/wiseconn-count',
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  deleteFarm: (id: string) => apiClient.request<{
    success: boolean;
    message: string;
  }>({
    path: `/farms/${id}`,
    method: 'DELETE',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const customDevices = {
  devicesList: (data: {
    page: number;
    limit: number;
    searchValue?: string;
    accountId?: string;
    farmId?: string;
    status?: string;
    sort?: string;
    sensorType?: string;
  }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', data.page.toString());
    if (data.limit) queryParams.append('limit', data.limit.toString());
    if (data.searchValue) queryParams.append('searchValue', data.searchValue);
    if (data.accountId) queryParams.append('accountId', data.accountId);
    if (data.farmId) queryParams.append('farmId', data.farmId);
    if (data.status) queryParams.append('status', data.status);
    if (data.sort) queryParams.append('sort', data.sort);
    if (data.sensorType) queryParams.append('sensorType', data.sensorType);
    return apiClient.request({
      path: `/devices?${queryParams.toString()}`,
      method: 'GET',
      secure: true,
      type: ContentType.Json,
      format: 'json',
    });
  },
};

export const exceptionRanges = {
  get: (sectorId: string) => apiClient.request({
    path: `/sector/${sectorId}/exception-ranges`,
    method: 'GET',
    secure: true,
    format: 'json',
  }),
  update: (sectorId: string, ranges: Array<{ startDate: string; endDate: string }>) => (
    apiClient.request({
      path: `/sector/${sectorId}/exception-ranges`,
      method: 'PUT',
      body: { exceptionRanges: ranges },
      secure: true,
      type: ContentType.Json,
      format: 'json',
    })
  ),
};

export const savedCustomGraphs = {
  getBySector: (sectorId: string) => apiClient.request({
    path: `/savedCustomGraphs/${sectorId}`,
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  create: (sectorId: string, data: {
    name: string;
    checked: string[];
    selectedDepths: Record<string, string[]>;
    startDate: string | null;
    endDate: string | null;
  }) => apiClient.request({
    path: `/savedCustomGraphs/${sectorId}`,
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  delete: (id: string) => apiClient.request({
    path: `/savedCustomGraphs/${id}`,
    method: 'DELETE',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export type PhenologicalStateNameItem = { id: string; name: string };

export type GetPhenologicalStateNamesResponse = {
  success: boolean;
  message: string;
  phenologicalStateNames: PhenologicalStateNameItem[];
};

export type UpsertPhenologicalStateNameResponse = {
  success: boolean;
  message: string;
  phenologicalStateName?: PhenologicalStateNameItem;
};

export const phenologicalStateName = {
  phenologicalStateNameList: () => apiClient.request<GetPhenologicalStateNamesResponse>({
    path: '/phenological-state-name',
    method: 'GET',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  phenologicalStateNameCreate: (data: { name: string }) => apiClient
    .request<UpsertPhenologicalStateNameResponse>({
    path: '/phenological-state-name',
    method: 'POST',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  phenologicalStateNameUpdate: (
    id: string,
    data: { name: string },
  ) => apiClient.request<UpsertPhenologicalStateNameResponse>({
    path: `/phenological-state-name/${id}`,
    method: 'PUT',
    body: data,
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  phenologicalStateNameDelete: (id: string) => apiClient
    .request<UpsertPhenologicalStateNameResponse>({
    path: `/phenological-state-name/${id}`,
    method: 'DELETE',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
};

export const documentation = {
  grantAccess: () => apiClient.request({
    path: '/documentation/access',
    method: 'POST',
    secure: true,
    type: ContentType.Json,
    format: 'json',
  }),
  revokeAccess: () => apiClient.request({
    path: '/documentation/logout',
    method: 'POST',
    type: ContentType.Json,
    format: 'json',
  }),
};

const api = {
  ...apiClient,
  coldHoursConfig,
  chillPortionsConfig,
  degreeDayConfig,
  biweeklyReport,
  alert,
  rainfallConfig,
  customNodes,
  customSensors,
  customSectors,
  customSensorModels,
  customFarms,
  customDevices,
  supportTicket,
  exceptionRanges,
  savedCustomGraphs,
  phenologicalStateName,
  irrigation,
  peakHour,
  documentation,
};

export default api;
